import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  convertCurrencyFormat,
  formatPropertyAddress,
  getKycData,
  haveValue,
  resolveIncomeSelection,
  toast,
} from "../../helper/common";
import { useNavigate, useParams } from "react-router-dom";
import backicon from "../../assets/img/back.png";
import useTranslator from "../../hooks/useTranslator";
import jsPDF from "jspdf";
import { captureReportPages } from "../../helper/captureReportPages";
import LandLordContext from "../../context/landlord/LandLordContext";
import AuthContext from "../../context/auth/AuthContext";
import CandidateSummary from "./CandidateSummary";
import axiosInstance from "../../services/authWrapper";
import { transformAwardSnapshot } from "../../Utils/transformAwardSnapshot";
import { getMissingDocsFromSnapshot } from "../../Utils/getMissingDocsFromSnapshot";
import TenantContext from "../../context/tenant/TenantContext";
import SkorList2 from "../tenant-profile/skorList2";
import Loader from "../../components/Loader";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Button } from "../../components/design";
import { getDefaultCandidacyTenants } from "../../helper/defaultCandidacyChecks";
import { getDefaultCandidacyIncome } from "../../helper/defaultCandidacyIncome";
import useDefaultCandidacyChecks from "../../hooks/useDefaultCandidacyChecks";

// Default decision styles match LinkedPropertiesPopup.
const CANDIDACY_DECISION_STYLES = {
  unselected: {
    backgroundColor: "#F7F7F7",
    borderColor: "#E6E6E6",
    badgeBg: "#253837",
    badgeText: "!",
  },
  approved: {
    backgroundColor: "#F2FBE9",
    borderColor: "#DAECC8",
    badgeBg: "#82CF3F",
    badgeText: "✓",
  },
  review: {
    backgroundColor: "#FFFBEC",
    borderColor: "#F2E09B",
    badgeBg: "#E1BB20",
    badgeText: "!",
  },
  declined: {
    backgroundColor: "#FFE2E2",
    borderColor: "#EB9B9C",
    badgeBg: "#FF3C3C",
    badgeText: "✕",
  },
};

const getCandidacyDisplayName = (tenant) => {
  if (haveValue(tenant?.forensic_name)) return tenant.forensic_name;
  const kycData = getKycData(tenant);
  if (haveValue(kycData?.first_name)) {
    return `${kycData.first_name} ${kycData.last_name ?? ""}`.trim();
  }
  return `${tenant?.fname ?? ""} ${tenant?.lname ?? ""}`.trim();
};

const CandidacyDecisionBanner = ({ status, title, subtitle, reasons }) => {
  const style =
    CANDIDACY_DECISION_STYLES[status] ||
    (status === "loading"
      ? CANDIDACY_DECISION_STYLES.unselected
      : CANDIDACY_DECISION_STYLES.review);
  return (
    <div className="mx-custom-gap" style={{ marginTop: "16px" }}>
      <div
        style={{
          backgroundColor: style.backgroundColor,
          border: `1px solid ${style.borderColor}`,
          borderRadius: "12px",
          padding: "16px 20px",
          display: "flex",
          gap: "12px",
          alignItems: "flex-start",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            backgroundColor: style.badgeBg,
            color: "#fff",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {status === "loading" ? (
            <LoadingSpinner size="16px" color="#fff" borderWidth={2} />
          ) : (
            style.badgeText
          )}
        </div>
        <div style={{ flex: 1, color: "#253837" }}>
          <div>
            <strong>{title}</strong>{" "}
            <span style={{ color: "#505B5B" }}>{subtitle}</span>
          </div>
          {reasons?.length > 0 && (
            <ul style={{ margin: "8px 0 0", paddingLeft: "18px" }}>
              {reasons.map((reason, index) => (
                <li
                  key={`${reason.name || "generic"}-${index}`}
                  style={{ marginBottom: "4px" }}
                >
                  {reason.name ? (
                    <>
                      <strong>{reason.name}: </strong>
                      <span>{reason.text}</span>
                    </>
                  ) : (
                    <span>{reason.text}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const ReportWrapper = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const {
    getPropertyDetails,
    fetchingCreatedTenant,
    deaWorkspace,
    getProfileResolutionStatus,
  } = useContext(LandLordContext);
  const { fetchTenantDocs } = useContext(TenantContext);
  const {
    user: originalUser,
    checkIeDetails,
    loadUser,
  } = useContext(AuthContext);
  const queryParameters = new URLSearchParams(window.location.search);
  const definedRent = queryParameters.get("rent") ?? 0;
  const sharedprofileid = queryParameters.getAll("sharedprofileid");
  const agencylink = queryParameters.getAll("agencylink");
  const guarantor_id =
    queryParameters.get("guarantor_id") ?? queryParameters.get("guarantor");
  const llCreated = queryParameters.get("llCreated") === "true";
  const property_id = queryParameters.get("property");
  const redirectUrl = queryParameters.get("redirectUrl");
  const selectionRaw = queryParameters.get("selection");
  const awardSnapshotCompleted =
    queryParameters.get("awardSnapshotCompleted") === "true";
  const historyEventId = queryParameters.get("historyEventId");

  const incomeSelectionModel = useMemo(() => {
    if (!selectionRaw) return null;
    try {
      const model = JSON.parse(decodeURIComponent(selectionRaw));
      return Array.isArray(model) ? model : null;
    } catch {
      return null;
    }
  }, [selectionRaw]);

  const selectionSelectedIds = useMemo(() => {
    if (!selectionRaw) return null;
    try {
      const model = JSON.parse(decodeURIComponent(selectionRaw));
      return model.flatMap((entry) => {
        if (entry.type === "tenant") return [entry.tenantId].filter(Boolean);
        return (entry.participants || [])
          .map((p) => p.tenantId)
          .filter(Boolean);
      });
    } catch {
      return null;
    }
  }, [selectionRaw]);

  const selectionSelectedGuarantorIds = useMemo(() => {
    if (!selectionRaw) return null;
    try {
      const model = JSON.parse(decodeURIComponent(selectionRaw));
      return model.flatMap((entry) => {
        if (entry.type === "tenant") return entry.guarantorIds || [];
        return (entry.participants || []).flatMap((p) => p.guarantorIds || []);
      });
    } catch {
      return null;
    }
  }, [selectionRaw]);

  const [addressVal, setAddressVal] = useState("");
  const [combinedMaxRent, setCombinedMaxRent] = useState(0);
  const [combinedEffortRate, setCombinedEffortRate] = useState(0);
  const [userIncomes, setUserIncomes] = useState({});
  const [userMaxRent, setUserMaxRent] = useState({});
  const [localLoading, setLocalLoading] = useState(true);

  const { id } = useParams();

  const [ids, setIds] = useState([]);
  const [type, setType] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const reportedMaxRentCount = Object.keys(userMaxRent).filter(
    (key) => haveValue(key) && key !== "undefined"
  ).length;

  useEffect(() => {
    const temp_vals = Object.values(userMaxRent);
    let temp_max_rent = 0;
    temp_vals.map((val) => (temp_max_rent += +val));
    setCombinedMaxRent(temp_max_rent);
  }, [userMaxRent]);

  useEffect(() => {
    setLocalLoading(true);
    let userType = localStorage.getItem("usertype");
    loadUser(userType);

    if (haveValue(sharedprofileid[0])) {
      setIds(sharedprofileid);
      setType("sharedprofileid");
    } else if (haveValue(agencylink[0])) {
      setIds(agencylink);
      setType("agencylink");
    } else if (llCreated) {
      setIds([id]);
      setType("llCreated");
    } else {
      setIds([id]);
    }
    if (haveValue(property_id)) {
      getPropertyDetails(property_id)
        .then(async (res) => {
          const propertyData = res.properties;
          setAddressVal(formatPropertyAddress(propertyData));

          if (awardSnapshotCompleted || historyEventId) {
            try {
              const snapshotRes = historyEventId
                ? await axiosInstance.get(
                    `${process.env.REACT_APP_SERVER_URL}/users/history-award-snapshot/${historyEventId}`
                  )
                : await axiosInstance.post(
                    `${process.env.REACT_APP_SERVER_URL}/users/award-snapshot`,
                    { propertyId: property_id }
                  );
              const snapshotData = snapshotRes.data?.data;
              const snapshotProfiles = transformAwardSnapshot(snapshotData);

              const snapshotCreatedAt =
                propertyData?.award_snapshot_info?.created_at;

              snapshotProfiles.forEach((profile) => {
                const tenantData = profile.tenantid;
                if (tenantData) {
                  tenantData._snapshotMissingDocs = getMissingDocsFromSnapshot(
                    tenantData,
                    deaWorkspace,
                    snapshotCreatedAt
                  );
                  if (tenantData.gurantor_id) {
                    tenantData.gurantor_id.forEach((g) => {
                      if (g && typeof g === "object") {
                        g._snapshotMissingDocs = getMissingDocsFromSnapshot(
                          g,
                          deaWorkspace,
                          snapshotCreatedAt
                        );
                      }
                    });
                  }
                }
                if (profile.application_id?.participants) {
                  profile.application_id.participants.forEach((p) => {
                    const pt = p.tenant_info;
                    if (pt) {
                      pt._snapshotMissingDocs = getMissingDocsFromSnapshot(
                        pt,
                        deaWorkspace,
                        snapshotCreatedAt
                      );
                      if (pt.gurantor_id) {
                        pt.gurantor_id.forEach((g) => {
                          if (g && typeof g === "object") {
                            g._snapshotMissingDocs = getMissingDocsFromSnapshot(
                              g,
                              deaWorkspace,
                              snapshotCreatedAt
                            );
                          }
                        });
                      }
                    }
                  });
                }
              });

              const users = snapshotProfiles.map((profile) => ({
                _id: profile._id,
                tenantid: profile.tenantid || null,
                tenant_id: profile.tenantid || null,
                application_id: profile.application_id || null,
              }));

              setAllUsers(users.filter(Boolean));
            } catch (snapshotErr) {
              console.error(
                "Snapshot fetch failed in ReportWrapper",
                snapshotErr
              );
            }
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, []);

  useEffect(() => {
    setCombinedEffortRate(
      Math.ceil((definedRent / combinedMaxRent ?? 1) * 100)
    );
    setLocalLoading(false);
  }, [combinedMaxRent]);

  const [isDownloading, setIsDownloading] = useState(false);
  const downloadRef = useRef(null);

  useEffect(() => () => downloadRef.current?.abort(), []);

  const printDocument = async () => {
    if (downloadRef.current) return;
    const container = document.getElementById("divToPrint");
    if (!container?.children.length) return;

    const controller = new AbortController();
    downloadRef.current = controller;
    setIsDownloading(true);

    try {
      // A4 in points (1pt = 1/72 inch)
      const A4_WIDTH = 595.28; // portrait width
      const A4_HEIGHT = 841.89; // portrait height

      // Always create PDF as A4 portrait
      let pdf = new jsPDF({
        orientation: "p",
        unit: "pt",
        format: "a4",
      });

      const completed = await captureReportPages(
        container,
        (canvas, index) => {
          const imgData = canvas.toDataURL("image/jpeg", 0.95);

          // Original element size
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;

          // Scale image to fit into A4 portrait
          const scale = Math.min(A4_WIDTH / imgWidth, A4_HEIGHT / imgHeight);

          const renderWidth = imgWidth * scale;
          const renderHeight = imgHeight * scale;

          // Center horizontally, top-align vertically
          const x = (A4_WIDTH - renderWidth) / 2;
          const y = 0;

          // Add page if not first
          if (index > 0) pdf.addPage("a4", "p");

          pdf.addImage(imgData, "JPEG", x, y, renderWidth, renderHeight);
        },
        controller.signal
      );
      if (!completed || controller.signal.aborted) return;

      // Save with your logic
      if (haveValue(property_id)) {
        pdf.save(`${addressVal}.pdf`);
      } else {
        let temp_user = expandedAllUsers[0];
        let user_data = getTenantFromReportUser(temp_user);

        if (
          haveValue(guarantor_id) &&
          Array.isArray(user_data?.gurantor_id) &&
          user_data?.gurantor_id?.length
        ) {
          const guarantor = user_data.gurantor_id.find(
            (u) => u._id?.toString() === guarantor_id
          );
          if (guarantor) user_data = guarantor;
        }

        const fileName =
          expandedAllUsers.length > 1
            ? "Skor Profiles"
            : `${user_data?.fname || ""} ${user_data?.lname || ""}`.trim();

        pdf.save(`${fileName}.pdf`);
      }
    } catch (err) {
      console.error("Error capturing element:", err);
    } finally {
      if (!controller.signal.aborted) setIsDownloading(false);
      if (downloadRef.current === controller) downloadRef.current = null;
    }
  };

  const [sharedProfileInfo, setSharedProfileInfo] = useState(null);
  const fetchUserById = async (id, type) => {
    try {
      if (llCreated) {
        const res = await fetchingCreatedTenant(id);
        const doc_data = await fetchFileData(res?.data?._id);
        res.data.doc_data = doc_data;
        return res.data;
      }

      const rawLandlordId = localStorage.getItem("saveduserid");
      const landlordId =
        rawLandlordId &&
        rawLandlordId !== "null" &&
        rawLandlordId !== "undefined" &&
        /^[a-f\d]{24}$/i.test(rawLandlordId)
          ? rawLandlordId
          : null;
      const lang = localStorage.getItem("i18nextLng");
      const headers = { "Accept-Language": lang };

      let url = "";

      if (type === "sharedprofileid") {
        url = `${process.env.REACT_APP_SERVER_URL}/shared-profile/singleshared/${id}${landlordId ? `?landlordId=${landlordId}` : ""}`;
      } else if (type === "agencylink") {
        url = `${process.env.REACT_APP_SERVER_URL}/shared-profile/agency-shared/${id}${landlordId ? `?landlordId=${landlordId}` : ""}`;
      } else {
        return originalUser;
      }

      const res = await axiosInstance.get(url, { headers });

      const user = res.data.singleSharedProfile;
      console.log("[ReportWrapper] fetchUserById response:", {
        id,
        type,
        hasApplicationId: !!user?.application_id,
        participantsCount: user?.application_id?.participants?.length || 0,
        participants: user?.application_id?.participants?.map((p) => ({
          id: p.id || p.tenant_info?._id,
          name: `${p.tenant_info?.fname || ""} ${p.tenant_info?.lname || ""}`.trim(),
          consent_status: p.consent_status,
          hasTenantInfo: !!p.tenant_info,
        })),
        tenantid: user?.tenantid
          ? `${user.tenantid.fname} ${user.tenantid.lname}`
          : null,
      });
      if (type === "sharedprofileid") {
        user.tenant_id = user?.tenantid;
      }
      setSharedProfileInfo(user);

      if (user?.tenantid?.ie_details_id) {
        user.tenantid.ie_verified = checkIeDetails(user.tenantid);
      }

      if (haveValue(user?.tenantid?._id)) {
        const doc_data = await fetchFileData(user.tenantid._id);
        user.tenantid.doc_data = doc_data;
      }
      return user;
    } catch (err) {
      if (err?.response?.data?.key === "insufficientCredits") {
        toast.error(_t(err?.response?.data?.key));
        navigate(-1);
      }
      console.error("Error fetching user by ID:", err);
      return null;
    }
  };

  const fetchFileData = async (user_id) => {
    if (!haveValue(user_id)) return [];
    const res = await fetchTenantDocs(user_id, llCreated);
    const tenantDocsData = res?.data?.success_res?.tenantDocs;
    if (haveValue(tenantDocsData) && tenantDocsData.length >= 1) {
      const temp_arr = new Map();
      tenantDocsData.map((docs) => {
        temp_arr.set(docs?.type + (docs?.year ? docs?.year : ""), docs);
      });

      return Array.from(temp_arr.values());
    }
    return [];
  };

  const fetchAllUsersInOrder = async (ids, type) => {
    setLocalLoading(true);
    const promises = ids.map((id) => fetchUserById(id, type));
    const users = await Promise.all(promises);

    setAllUsers(users);
    setLocalLoading(false);
  };

  useEffect(() => {
    if (awardSnapshotCompleted || historyEventId) return;
    if (Array.isArray(ids) && ids.length > 0) {
      fetchAllUsersInOrder(ids, type);
    }
  }, [originalUser, ids, combinedMaxRent]);

  const handleBack = () => {
    if (redirectUrl) {
      navigate(redirectUrl);
    } else if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/home");
    }
  };

  function getTenantFromReportUser(reportUser) {
    return reportUser?.tenant_id ?? reportUser?.tenantid ?? reportUser;
  }

  function getGuarantorsFromReportUser(reportUser) {
    const tenant = getTenantFromReportUser(reportUser);
    return Array.isArray(tenant?.gurantor_id) ? tenant.gurantor_id : [];
  }

  const isApplicationProfile = (profile) =>
    profile?.application_id?.participants?.length > 0;

  const shouldRenderGuarantor = (guarantor) =>
    !haveValue(guarantor_id) || guarantor?._id?.toString() === guarantor_id;

  const expandedAllUsers = useMemo(() => {
    const expanded = allUsers.filter(Boolean).flatMap((reportUser) => {
      if (isApplicationProfile(reportUser)) {
        const acceptedParticipants =
          reportUser.application_id.participants.filter(
            (p) => p.consent_status === "accepted" && p.tenant_info
          );
        return acceptedParticipants.map((p) => ({
          ...reportUser,
          tenantid: p.tenant_info,
          tenant_id: p.tenant_info,
          income_selection:
            resolveIncomeSelection(
              reportUser.income_selection,
              p.tenant_info?._id
            ) ||
            p.income_selection ||
            reportUser.income_selection,
        }));
      }
      return [reportUser];
    });
    if (selectionSelectedIds) {
      return expanded
        .filter((u) =>
          selectionSelectedIds.includes(
            u?.tenantid?._id || u?.tenant_id?._id || u?._id
          )
        )
        .map((u) => {
          if (selectionSelectedGuarantorIds && u?.tenant_id?.gurantor_id) {
            const filtered = u.tenant_id.gurantor_id.filter((g) =>
              selectionSelectedGuarantorIds.includes(g?._id)
            );
            return {
              ...u,
              tenant_id: { ...u.tenant_id, gurantor_id: filtered },
              tenantid: { ...u.tenantid, gurantor_id: filtered },
            };
          }
          return u;
        });
    }
    if (selectionSelectedGuarantorIds) {
      return expanded.map((u) => {
        if (u?.tenant_id?.gurantor_id) {
          const filtered = u.tenant_id.gurantor_id.filter((g) =>
            selectionSelectedGuarantorIds.includes(g?._id)
          );
          return {
            ...u,
            tenant_id: { ...u.tenant_id, gurantor_id: filtered },
            tenantid: { ...u.tenantid, gurantor_id: filtered },
          };
        }
        return u;
      });
    }
    return expanded;
  }, [allUsers, selectionSelectedIds, selectionSelectedGuarantorIds]);

  const expectedTenantCount = useMemo(() => {
    const validUsers = allUsers.filter(Boolean);
    if (validUsers.length === 0) return ids.length;
    return validUsers.reduce((count, reportUser) => {
      if (isApplicationProfile(reportUser)) {
        return (
          count +
          reportUser.application_id.participants.filter(
            (p) => p.consent_status === "accepted" && p.tenant_info
          ).length
        );
      }
      return count + 1;
    }, 0);
  }, [allUsers, ids]);

  const isCombinedMaxRentCalculating =
    ids.length === 0 || reportedMaxRentCount < expectedTenantCount;

  console.log("[ReportWrapper] State:", {
    ids,
    type,
    allUsersCount: allUsers.filter(Boolean).length,
    expandedAllUsersCount: expandedAllUsers.length,
    expectedTenantCount,
    reportedMaxRentCount,
    isCombinedMaxRentCalculating:
      ids.length === 0 || reportedMaxRentCount < expectedTenantCount,
  });

  const reportProfiles = useMemo(() => {
    const renderedGuarantorIds = new Set();
    const profiles = [];
    ids.forEach((profileId, index) => {
      const reportUser = allUsers[index];
      if (!reportUser) return;

      if (isApplicationProfile(reportUser)) {
        const acceptedParticipants =
          reportUser.application_id.participants.filter(
            (p) => p.consent_status === "accepted" && p.tenant_info
          );
        acceptedParticipants.forEach((participant) => {
          const participantUser = {
            ...reportUser,
            tenantid: participant.tenant_info,
            tenant_id: participant.tenant_info,
            income_selection:
              resolveIncomeSelection(
                reportUser.income_selection,
                participant.tenant_info?._id
              ) ||
              participant.income_selection ||
              reportUser.income_selection,
          };
          const guarantors = getGuarantorsFromReportUser(
            participantUser
          ).filter((guarantor) => {
            const guarantorId = guarantor?._id?.toString();
            if (!shouldRenderGuarantor(guarantor)) return false;
            if (!haveValue(guarantorId)) return true;
            if (renderedGuarantorIds.has(guarantorId)) return false;
            renderedGuarantorIds.add(guarantorId);
            return true;
          });
          const filteredGuarantors = selectionSelectedGuarantorIds
            ? guarantors.filter((g) =>
                selectionSelectedGuarantorIds.includes(g?._id)
              )
            : guarantors;
          profiles.push({
            profileId,
            reportUser: participantUser,
            guarantors: filteredGuarantors,
            reportGuarantorIds: filteredGuarantors
              .map((g) => g?._id?.toString())
              .filter(haveValue)
              .join(","),
            participantTenantId: participant.tenant_info?._id,
          });
        });
      } else {
        const guarantors = getGuarantorsFromReportUser(reportUser).filter(
          (guarantor) => {
            const guarantorId = guarantor?._id?.toString();
            if (!shouldRenderGuarantor(guarantor)) return false;
            if (!haveValue(guarantorId)) return true;
            if (renderedGuarantorIds.has(guarantorId)) return false;
            renderedGuarantorIds.add(guarantorId);
            return true;
          }
        );
        const filteredGuarantors = selectionSelectedGuarantorIds
          ? guarantors.filter((g) =>
              selectionSelectedGuarantorIds.includes(g?._id)
            )
          : guarantors;
        profiles.push({
          profileId,
          reportUser,
          guarantors: filteredGuarantors,
          mainTenantId:
            reportUser?.tenant_id ?? reportUser?.tenantid ?? reportUser,
          reportGuarantorIds: filteredGuarantors
            .map((g) => g?._id?.toString())
            .filter(haveValue)
            .join(","),
        });
      }
    });
    return profiles;
  }, [ids, allUsers, selectionSelectedGuarantorIds]);

  const filteredReportProfiles = selectionSelectedIds
    ? reportProfiles.filter((p) =>
        selectionSelectedIds.includes(
          p.participantTenantId ||
            p.mainTenantId?._id ||
            p.reportUser?.tenantid?._id
        )
      )
    : reportProfiles;

  console.log("[ReportWrapper] Report profiles:", {
    count: reportProfiles.length,
    profiles: reportProfiles.map((p) => ({
      profileId: p.profileId,
      tenantName:
        `${p.reportUser?.tenantid?.fname || ""} ${p.reportUser?.tenantid?.lname || ""}`.trim(),
      participantTenantId: p.participantTenantId || null,
      guarantorCount: p.guarantors.length,
    })),
  });

  const defaultRulesEnabled = Boolean(
    originalUser?.current_workspace &&
    originalUser.current_workspace.dea_rules_activated !== true &&
    !deaWorkspace &&
    originalUser.current_workspace.recommendation_rules_activated !== true
  );
  const candidacyTenants = useMemo(
    () => getDefaultCandidacyTenants(allUsers, selectionSelectedIds),
    [allUsers, selectionSelectedIds]
  );
  const candidacyIncome = useMemo(
    () =>
      defaultRulesEnabled
        ? getDefaultCandidacyIncome(allUsers, {
            user: originalUser,
            _t,
            rent: definedRent,
            selectionModel: incomeSelectionModel,
          })
        : null,
    [
      allUsers,
      originalUser,
      _t,
      definedRent,
      incomeSelectionModel,
      defaultRulesEnabled,
    ]
  );
  const {
    status,
    titleKey,
    defaulters,
    pendingVerification,
    effortRateExceeded,
  } = useDefaultCandidacyChecks(
    candidacyTenants,
    getProfileResolutionStatus,
    originalUser?.current_workspace?._id,
    defaultRulesEnabled,
    "ReportWrapper",
    defaultRulesEnabled ? candidacyIncome : null
  );

  useEffect(() => {
    console.info("[CandidacyDecision][ReportWrapper] selection", {
      selectedTenantIds: selectionSelectedIds,
      evaluatedTenantIds: candidacyTenants.map((tenant) => tenant._id),
      profiles: allUsers.filter(Boolean).map((profile) => ({
        profileId: profile._id,
        tenantId: (profile.tenantid ?? profile.tenant_id ?? profile)?._id,
        applicationId: profile.application_id?._id,
        participants: (profile.application_id?.participants || []).map(
          (participant) => ({
            tenantId: participant?.tenant_info?._id ?? participant?.id,
            consentStatus: participant?.consent_status,
            deleted: Boolean(participant?.is_deleted || participant?.deleted),
            hasTenantInfo: Boolean(participant?.tenant_info),
          })
        ),
      })),
    });
  }, [allUsers, selectionSelectedIds, candidacyTenants]);

  const candidacyDecision = useMemo(() => {
    if (
      !defaultRulesEnabled ||
      allUsers.length === 0 ||
      allUsers.some((profile) => !profile)
    ) {
      return null;
    }

    if (status === "unselected" || status === "loading") {
      return {
        status,
        title: _t(titleKey),
        reasons: [],
      };
    }

    const translateReport = (key, defaultValue) =>
      _t(key, defaultValue, "translation");

    if (status === "declined") {
      return {
        status: "declined",
        title: _t(titleKey),
        subtitle: translateReport(
          "candidacyResult",
          "Resultado de la candidatura"
        ),
        reasons: [
          ...defaulters.map((tenant) => ({
            name: getCandidacyDisplayName(tenant),
            text: translateReport(
              "hasRegisteredDefaults",
              "tiene impagos registrados."
            ),
          })),
          ...(effortRateExceeded
            ? [
                {
                  text: translateReport(
                    "defaultEffortRateAbove50",
                    "El esfuerzo global supera el 50%."
                  ),
                },
              ]
            : []),
        ],
      };
    }

    if (status === "review") {
      return {
        status: "review",
        title: _t(titleKey),
        subtitle: translateReport(
          "candidacyResult",
          "Resultado de la candidatura"
        ),
        reasons: pendingVerification.map((tenant) => ({
          name: getCandidacyDisplayName(tenant),
          text: translateReport(
            "verificationPendingReview",
            "verificación pendiente de revisión."
          ),
        })),
      };
    }

    return {
      status: "approved",
      title: _t(titleKey),
      subtitle: translateReport(
        "candidacyResult",
        "Resultado de la candidatura"
      ),
      reasons: [
        {
          text: translateReport(
            "allTenantsAndGuarantorsMeetSkorCriteria",
            "Todos los inquilinos y avalistas cumplen los criterios de Skor."
          ),
        },
      ],
    };
  }, [
    allUsers,
    defaultRulesEnabled,
    status,
    titleKey,
    defaulters,
    pendingVerification,
    effortRateExceeded,
    _t,
  ]);

  return (
    <>
      {localLoading ? (
        <Loader />
      ) : (
        <div className="tabs-box-pattern-2">
          <div className="tabs-item-1 d-block">
            <div className="row">
              <div className="header-edit text-center report-header space-custom d-flex items-center justify-content-between position-relative">
                <div>
                  {" "}
                  <div className="cursor-pointer" onClick={handleBack}>
                    <img src={backicon} alt="backbtn" />
                  </div>
                </div>
                <div
                  className="pdf-qrcode position-absolute"
                  style={{
                    right: "1.875rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <div>
                    <Button
                      variant="accent"
                      size="md"
                      id="downloadpdf"
                      onClick={printDocument}
                      loading={isDownloading}
                      disabled={defaultRulesEnabled && status === "loading"}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          d="M4.5 0C3.25898 0 2.25 1.00898 2.25 2.25V5.625H4.5V2.25H12.4699L13.5 3.28008V5.625H15.75V3.28008C15.75 2.68242 15.5145 2.10938 15.0926 1.6875L14.0625 0.657422C13.6406 0.235547 13.0676 0 12.4699 0H4.5ZM13.5 12.375V15.75H4.5V12.375H13.5ZM15.75 13.5H16.875C17.4973 13.5 18 12.9973 18 12.375V9C18 7.75898 16.991 6.75 15.75 6.75H2.25C1.00898 6.75 0 7.75898 0 9V12.375C0 12.9973 0.502734 13.5 1.125 13.5H2.25V15.75C2.25 16.991 3.25898 18 4.5 18H13.5C14.741 18 15.75 16.991 15.75 15.75V13.5ZM15.1875 8.71875C15.4113 8.71875 15.6259 8.80764 15.7841 8.96588C15.9424 9.12411 16.0312 9.33872 16.0312 9.5625C16.0312 9.78628 15.9424 10.0009 15.7841 10.1591C15.6259 10.3174 15.4113 10.4062 15.1875 10.4062C14.9637 10.4062 14.7491 10.3174 14.5909 10.1591C14.4326 10.0009 14.3438 9.78628 14.3438 9.5625C14.3438 9.33872 14.4326 9.12411 14.5909 8.96588C14.7491 8.80764 14.9637 8.71875 15.1875 8.71875Z"
                          fill="#253837"
                        />
                      </svg>
                      {_t("downloadPDF")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div id="divToPrint" className="row">
              {haveValue(property_id) && (
                <>
                  <div className="pt-5">
                    <div className="report-main align-items-center d-flex justify-content-between">
                      <h3>{_t("candidacyReport")}</h3>
                      <img src="/images/SkorWhite.svg" alt="" />
                    </div>
                    <div className="mx-custom-gap report-header-border w-fit">
                      <div className="report-content">
                        <div className="img-comp">
                          <img
                            src="/images/house-star.svg"
                            className="house-start-img"
                            alt="icon"
                          />
                          <div className="d-flex flex-column padd-3px">
                            <span className="pdf-upr-stripe-label">
                              {_t("address")}
                            </span>
                            <span className="pdf-upr-stripe-value nowrap-text">
                              {addressVal}
                            </span>
                          </div>
                        </div>

                        <div className="d-flex flex-column padd-3px">
                          <span className="pdf-upr-stripe-label">
                            {_t("rentPriceReport")}
                          </span>
                          <span className="pdf-upr-stripe-value">
                            {convertCurrencyFormat(definedRent)}€
                          </span>
                        </div>

                        <div className="d-flex flex-column padd-3px">
                          <span className="pdf-upr-stripe-label">
                            {" "}
                            {_t(
                              "combinedSalary",
                              "while generating report for multi tenants"
                            )}
                          </span>
                          <div className="d-flex gap-20px">
                            <span className="pdf-upr-stripe-value">
                              {defaultRulesEnabled
                                ? `${convertCurrencyFormat(candidacyIncome.totalIncome)}€`
                                : isCombinedMaxRentCalculating
                                  ? _t("calculating")
                                  : `${convertCurrencyFormat(
                                      parseFloat(combinedMaxRent.toFixed(2))
                                    )}€`}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="candidate-details candidateDetails">
                    {candidacyDecision && (
                      <CandidacyDecisionBanner
                        status={candidacyDecision.status}
                        title={candidacyDecision.title}
                        subtitle={candidacyDecision.subtitle}
                        reasons={candidacyDecision.reasons}
                      />
                    )}
                    <div className="mx-custom-gap p-0">
                      <>
                        <CandidateSummary
                          allUsers={expandedAllUsers}
                          definedRent={definedRent}
                          originalUser={originalUser}
                          candidacyIncome={
                            defaultRulesEnabled ? candidacyIncome : null
                          }
                        />
                      </>
                    </div>
                  </div>
                </>
              )}
              {filteredReportProfiles.map(
                (
                  {
                    profileId,
                    guarantors,
                    reportGuarantorIds,
                    participantTenantId,
                    mainTenantId,
                    reportUser,
                  },
                  index
                ) => {
                  return (
                    <Fragment key={`${profileId}-${index}`}>
                      <div className="pt-0">
                        <div className="d-flex justify-content-center pb-3">
                          <img src="/images/SkorWhite.svg" alt="" />
                        </div>
                        <SkorList2
                          sharedprofileid={
                            type === "sharedprofileid" ? profileId : null
                          }
                          agencylink={type === "agencylink" ? profileId : null}
                          participantTenantId={participantTenantId}
                          preloadedProfile={
                            awardSnapshotCompleted ? reportUser : null
                          }
                          reportMode={awardSnapshotCompleted}
                          setUserMaxRent={setUserMaxRent}
                          setUserIncomes={setUserIncomes}
                          showReportIcons={false}
                          reportGuarantorIds={reportGuarantorIds}
                        />
                      </div>

                      {guarantors.map((guarantor, guarantorIndex) => (
                        <div
                          className="pt-0"
                          key={`${profileId}-${guarantor?._id ?? guarantorIndex}`}
                        >
                          <div className="d-flex justify-content-center pb-3">
                            <img src="/images/SkorWhite.svg" alt="" />
                          </div>
                          <SkorList2
                            sharedprofileid={
                              type === "sharedprofileid" ? profileId : null
                            }
                            agencylink={
                              type === "agencylink" ? profileId : null
                            }
                            participantTenantId={participantTenantId}
                            preloadedProfile={
                              awardSnapshotCompleted ? reportUser : null
                            }
                            reportMode={awardSnapshotCompleted}
                            guarantor={guarantor?._id}
                            setUserMaxRent={() => {}}
                            setUserIncomes={setUserIncomes}
                            showReportIcons={false}
                            mainTenantId={mainTenantId}
                          />
                        </div>
                      ))}
                    </Fragment>
                  );
                }
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportWrapper;
